/**
 * <s-product-gallery> — PDP main-image slider with a thumbnail strip.
 *
 * Commands (sent from buttons via commandfor/command):
 *   --go-to  data-index on the source button selects that slide
 *   --prev / --next step one slide
 *
 * Required markup:
 *   [data-gallery-track]  scroll-snap track
 *   [data-gallery-slide]  one per media item, in track order
 *   [data-gallery-thumb]  optional thumbnails with data-index (aria-current tracks the active slide)
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', this.#handleCommand, { signal: this.#ac.signal });

    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;

    // Track the slide that is mostly in view so swipes and arrows keep thumbnails in sync.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.#slides().indexOf(entry.target));
        }
      },
      { root: track, threshold: 0.6 },
    );
    for (const slide of this.#slides()) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #slides() {
    return [...this.querySelectorAll('[data-gallery-slide]')];
  }

  #handleCommand = (event) => {
    const slides = this.#slides();
    if (!slides.length) return;

    let target = this.#index;
    if (event.command === '--go-to') target = Number(event.source?.dataset.index ?? 0);
    else if (event.command === '--prev') target = (this.#index - 1 + slides.length) % slides.length;
    else if (event.command === '--next') target = (this.#index + 1) % slides.length;
    else return;

    this.#goTo(target);
  };

  #goTo(index) {
    const slide = this.#slides()[index];
    const track = this.querySelector('[data-gallery-track]');
    if (!slide || !track) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll the track itself so the page never jumps vertically.
    const offset = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollBy({ left: offset, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      if (Number(thumb.dataset.index) === index) {
        thumb.setAttribute('aria-current', 'true');
        const strip = thumb.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth) {
          const delta = thumb.getBoundingClientRect().left - strip.getBoundingClientRect().left - strip.clientWidth / 2 + thumb.clientWidth / 2;
          strip.scrollBy({ left: delta });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
    for (const dot of this.querySelectorAll('[data-gallery-dot]')) {
      dot.toggleAttribute('data-active', Number(dot.dataset.index) === index);
    }
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
